import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  AppAutocomplete,
  AppButton,
  AppTextField,
  AppToggleField,
} from "../../../components/form";
import {
  AppActionsRow,
  AppFormField,
  AppFormGrid,
  PageSectionCard,
} from "../../../components/layout";
import { PageHeader, PageListContainer } from "../../../components/Page";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import { recipientDomainNotAllowedText } from "../../../utils/notification";
import {
  mailSettingsService,
  type MailProvider,
  type MailSettings,
  type MailSettingsSavePayload,
} from "../../../services/mailSettingsService";

type ProviderOption = { value: MailProvider; label: string };
type PortOption = { value: number; label: string };

const SMTP_PORT_OPTIONS: PortOption[] = [
  { value: 587, label: "587 (STARTTLS)" },
  { value: 465, label: "465 (SSL/TLS)" },
  { value: 25, label: "25" },
  { value: 2525, label: "2525" },
];

function mapFormFromResponse(data: MailSettings): MailSettingsSavePayload {
  return {
    enabled: !!data.enabled,
    provider:
      data.provider === "OFFICE365_GRAPH" ? "OFFICE365_GRAPH" : "CUSTOM_SMTP",
    host: data.host || "",
    port: data.port || 587,
    username: data.username || "",
    fromAddress: data.fromAddress || "",
    startTls: data.startTls !== false,
    tenantId: data.tenantId || "",
    clientId: data.clientId || "",
    allowedRecipientDomains: data.allowedRecipientDomains || [],
  };
}

function MailSettingsPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorWarning, setErrorWarning] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const [form, setForm] = useState<MailSettingsSavePayload>({
    enabled: false,
    provider: "CUSTOM_SMTP",
    host: "",
    port: 587,
    username: "",
    fromAddress: "",
    startTls: true,
    tenantId: "",
    clientId: "",
    allowedRecipientDomains: [],
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [clientSecretSet, setClientSecretSet] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState("");

  const isGraph = form.provider === "OFFICE365_GRAPH";

  const providerOptions = useMemo<ProviderOption[]>(
    () => [
      { value: "CUSTOM_SMTP", label: t("mailSettings.providerSmtp") },
      { value: "OFFICE365_GRAPH", label: t("mailSettings.providerO365") },
    ],
    [t],
  );

  const selectedProvider =
    providerOptions.find((option) => option.value === form.provider) ??
    providerOptions[0];

  const selectedPort =
    SMTP_PORT_OPTIONS.find((option) => option.value === form.port) ??
    ({ value: form.port, label: String(form.port) } satisfies PortOption);

  const portOptions = useMemo(() => {
    if (SMTP_PORT_OPTIONS.some((option) => option.value === form.port)) {
      return SMTP_PORT_OPTIONS;
    }
    return [
      ...SMTP_PORT_OPTIONS,
      { value: form.port, label: String(form.port) },
    ];
  }, [form.port]);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    setErrorWarning(false);
    try {
      const data: MailSettings = await mailSettingsService.get();
      setForm(mapFormFromResponse(data));
      setDomains(data.allowedRecipientDomains || []);
      setPasswordSet(!!data.passwordSet);
      setClientSecretSet(!!data.clientSecretSet);
      setPassword("");
      setClientSecret("");
    } catch (e) {
      setErrorWarning(false);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const normalizeDomains = (values: Array<string | unknown>): string[] => {
    const unique: string[] = [];
    for (const raw of values) {
      if (typeof raw !== "string") continue;
      const cleaned = raw.trim().toLowerCase().replace(/^@/, "");
      if (cleaned && !unique.includes(cleaned)) {
        unique.push(cleaned);
      }
    }
    return unique;
  };

  const addDomain = () => {
    const next = normalizeDomains([...domains, domainInput]);
    if (next.length === domains.length && domainInput.trim()) {
      // Duplicate or already present — still clear the input.
      setDomainInput("");
      return;
    }
    setDomains(next);
    setDomainInput("");
  };

  const removeDomain = async (domain: string) => {
    try {
      await confirmDelete({ itemName: domain });
      setDomains((prev) => prev.filter((item) => item !== domain));
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setErrorWarning(false);
    setSuccess(null);
    try {
      const payload: MailSettingsSavePayload = {
        ...form,
        port: Number(form.port) > 0 ? Number(form.port) : 587,
        allowedRecipientDomains: normalizeDomains(domains),
      };
      if (password.trim()) {
        payload.password = password.trim();
      }
      if (clientSecret.trim()) {
        payload.clientSecret = clientSecret.trim();
      }
      const saved = await mailSettingsService.save(payload);
      setPasswordSet(!!saved.passwordSet);
      setClientSecretSet(!!saved.clientSecretSet);
      setPassword("");
      setClientSecret("");
      setForm(mapFormFromResponse(saved));
      setDomains(saved.allowedRecipientDomains || []);
      setSuccess(t("mailSettings.saveSuccess"));
    } catch (e) {
      setErrorWarning(false);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) {
      setErrorWarning(false);
      setError(t("mailSettings.testToRequired"));
      return;
    }
    setTesting(true);
    setError(null);
    setErrorWarning(false);
    setSuccess(null);
    try {
      // Persist current form first so test uses Graph/SMTP values just entered (secrets included).
      const payload: MailSettingsSavePayload = {
        ...form,
        port: Number(form.port) > 0 ? Number(form.port) : 587,
        allowedRecipientDomains: normalizeDomains(domains),
      };
      if (password.trim()) {
        payload.password = password.trim();
      }
      if (clientSecret.trim()) {
        payload.clientSecret = clientSecret.trim();
      }
      const saved = await mailSettingsService.save(payload);
      setPasswordSet(!!saved.passwordSet);
      setClientSecretSet(!!saved.clientSecretSet);
      setPassword("");
      setClientSecret("");
      setForm(mapFormFromResponse(saved));
      setDomains(saved.allowedRecipientDomains || []);

      await mailSettingsService.sendTest(testTo.trim());
      setSuccess(t("mailSettings.testSuccess"));
    } catch (e) {
      const warning = recipientDomainNotAllowedText((e as { response?: { data?: unknown } })?.response?.data);
      setErrorWarning(Boolean(warning));
      setError(warning || (e instanceof Error ? e.message : String(e)));
    } finally {
      setTesting(false);
    }
  };

  return (
    <PageListContainer>
      <PageHeader
        title={t("mailSettings.title")}
        subtitle={t("mailSettings.subtitle")}
      />

      {error && (
        <Alert severity={errorWarning ? "warning" : "error"}  onClose={() => { setError(null); setErrorWarning(false); }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Stack spacing={2}>
        <PageSectionCard title={t("mailSettings.connectionSection")}>
          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <AppFormGrid>
                <AppFormField size={GRID_HALF}>
                  <AppToggleField
                    label={t("mailSettings.enabled")}
                    value={form.enabled ? "true" : "false"}
                    onChange={(v) =>
                      setForm((prev) => ({ ...prev, enabled: v === "true" }))
                    }
                    options={[
                      { value: "true", label: t("common.yes") },
                      { value: "false", label: t("common.no") },
                    ]}
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppAutocomplete<ProviderOption>
                    label={t("mailSettings.provider")}
                    options={providerOptions}
                    value={selectedProvider}
                    disableClearable
                    autoHighlight
                    openOnFocus
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    onChange={(_, option) => {
                      if (!option) return;
                      setForm((prev) => ({ ...prev, provider: option.value }));
                    }}
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("mailSettings.fromAddress")}
                    value={form.fromAddress || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        fromAddress: e.target.value,
                      }))
                    }
                    fullWidth
                    helperText={
                      isGraph
                        ? t("mailSettings.fromAddressGraphHint")
                        : undefined
                    }
                  />
                </AppFormField>

                {isGraph ? (
                  <>
                    <AppFormField size={GRID_FULL}>
                      <Alert severity="info">
                        {t("mailSettings.o365Help")}
                      </Alert>
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("mailSettings.tenantId")}
                        value={form.tenantId || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            tenantId: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("mailSettings.clientId")}
                        value={form.clientId || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            clientId: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={
                          clientSecretSet
                            ? t("mailSettings.clientSecretKeep")
                            : t("mailSettings.clientSecret")
                        }
                        type="password"
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        fullWidth
                        autoComplete="new-password"
                        helperText={
                          clientSecretSet
                            ? t("mailSettings.clientSecretHint")
                            : undefined
                        }
                      />
                    </AppFormField>
                  </>
                ) : (
                  <>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("mailSettings.host")}
                        value={form.host || ""}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, host: e.target.value }))
                        }
                        fullWidth
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <Autocomplete
                        options={portOptions}
                        value={selectedPort}
                        disableClearable
                        autoHighlight
                        openOnFocus
                        freeSolo
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.label
                        }
                        isOptionEqualToValue={(a, b) => {
                          const left =
                            typeof a === "string" ? Number(a) : a.value;
                          const right =
                            typeof b === "string" ? Number(b) : b.value;
                          return left === right;
                        }}
                        onChange={(_, option) => {
                          if (option == null) return;
                          const next =
                            typeof option === "string"
                              ? Number(option)
                              : option.value;
                          setForm((prev) => ({
                            ...prev,
                            port:
                              Number.isFinite(next) && next > 0 ? next : 587,
                          }));
                        }}
                        onInputChange={(_, value, reason) => {
                          if (reason !== "input") return;
                          const next = Number(value);
                          if (Number.isFinite(next) && next > 0) {
                            setForm((prev) => ({ ...prev, port: next }));
                          }
                        }}
                        renderInput={(params) => (
                          <AppTextField
                            {...params}
                            label={t("mailSettings.port")}
                          />
                        )}
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("mailSettings.username")}
                        value={form.username || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            username: e.target.value,
                          }))
                        }
                        fullWidth
                        autoComplete="off"
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={
                          passwordSet
                            ? t("mailSettings.passwordKeep")
                            : t("mailSettings.password")
                        }
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        autoComplete="new-password"
                        helperText={
                          passwordSet
                            ? t("mailSettings.passwordHint")
                            : undefined
                        }
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppToggleField
                        label={t("mailSettings.startTls")}
                        value={form.startTls ? "true" : "false"}
                        onChange={(v) =>
                          setForm((prev) => ({
                            ...prev,
                            startTls: v === "true",
                          }))
                        }
                        options={[
                          { value: "true", label: t("common.yes") },
                          { value: "false", label: t("common.no") },
                        ]}
                      />
                    </AppFormField>
                  </>
                )}

                <AppFormField size={GRID_FULL}>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "flex-start" }}
                    >
                      <AppTextField
                        label={t("mailSettings.allowedDomains")}
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addDomain();
                          }
                        }}
                        fullWidth
                        helperText={t("mailSettings.allowedDomainsHint")}
                      />
                      <AppButton
                        variant="outlined"
                        onClick={addDomain}
                        disabled={!domainInput.trim()}
                        sx={{ flexShrink: 0, mt: { sm: 0.5 } }}
                      >
                        {t("mailSettings.addDomain")}
                      </AppButton>
                    </Stack>
                    {domains.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
                        {domains.map((domain) => (
                          <Chip
                            key={domain}
                            label={domain}
                            size="small"
                            onDelete={() => removeDomain(domain)}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        {t("mailSettings.allowedDomainsEmpty")}
                      </Alert>
                    )}
                  </Stack>
                </AppFormField>
              </AppFormGrid>
              <AppActionsRow>
                <AppButton
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? t("common.saving") : t("common.save")}
                </AppButton>
              </AppActionsRow>
            </Stack>
          )}
        </PageSectionCard>

        <PageSectionCard title={t("mailSettings.testSection")}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems="flex-start"
          >
            <AppTextField
              label={t("mailSettings.testTo")}
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              fullWidth
              sx={{ maxWidth: 360 }}
            />
            <AppButton
              variant="outlined"
              onClick={handleTest}
              disabled={testing || saving || loading}
            >
              {testing ? t("mailSettings.testing") : t("mailSettings.sendTest")}
            </AppButton>
          </Stack>
        </PageSectionCard>
      </Stack>
    </PageListContainer>
  );
}

export default MailSettingsPage;
